import { useRef, useState } from 'react'
import PmrE2EProjectDetailsOrderlineModel from '../../interfaces/pmr-e2e-project-details-orderline-model'
import UserSettingsService from '../../../../../services/UserSettingsService'
import { uniq } from 'lodash'
import { Nullable } from '../../../../../utils/helpers/ts.helpers'
import { HandlerAssignmentNotificationEmailEditorDialogRef } from './HandlerAssignmentNotificationEmailEditorDialog'
import { authProvider } from '../../../../../providers/authProvider'
import { E2EUserPaginatedModel } from '../../../../../shared/models/E2EUserPaginatedModel'
import { MailModel } from '../../../../../shared/models/Mail.model'
import useToastr from '../../../../../hooks/useToastr'
import { fixedEncodeURIComponent } from '../../../../../utils/helpers/url.helpers'

type Props = {
  onSuppressed?: () => void
  emailEditorRef: React.MutableRefObject<HandlerAssignmentNotificationEmailEditorDialogRef | null>
}

type BuildDefaultMailProps = {
  selectedNewHandler: Nullable<E2EUserPaginatedModel>
  orderlines: PmrE2EProjectDetailsOrderlineModel[]
  loggedInUserEmail: string
}

export const MAX_SUBJECT_LENGTH = 255

export const useHandlerAssignmentNotificationEmailEditor = ({
  onSuppressed,
  emailEditorRef,
}: Props) => {
  const userInfo = authProvider.getAccountInfo()
  const loggedInUserEmail = userInfo?.account.idToken?.email
  const { showSuccess, showInfo } = useToastr()

  const [emailEditorVisible, setEmailEditorVisible] = useState(false)
  const [emailEditorOrderlines, setEmailEditorOrderLines] = useState<
    PmrE2EProjectDetailsOrderlineModel[]
  >([])

  const buildDefaultSubject = ({ orderlines }: Pick<BuildDefaultMailProps, 'orderlines'>) => {
    const modelNames = orderlines
      .map(orderline => orderline.modelNameCsv)
      .filter(modelName => !!modelName)
      .join(', ')

    const countries = uniq(orderlines.map(orderline => orderline.certificateScheme)).join(', ')
    const defaultSubject =
      `[${orderlines[0]?.soldToCustomer}]${modelNames ? `[${modelNames}]` : ''}[${countries}]`.substring(
        0,
        MAX_SUBJECT_LENGTH
      )

    return defaultSubject
  }

  const buildDefaultBody = ({
    selectedNewHandler,
    orderlines,
  }: Pick<BuildDefaultMailProps, 'selectedNewHandler' | 'orderlines'>) => {
    const html = String.raw

    const CELL_STYLE = 'border: 1px solid #ccc; padding: 5px 8px; text-align: center !important;'
    const columns: {
      header: string
      value?: (orderline: PmrE2EProjectDetailsOrderlineModel) => Nullable<string>
      rawHtml?: boolean
    }[] = [
      {
        header: 'Order Number',
        value: orderline => orderline.orderNumber,
      },
      {
        header: 'Order Line Number',
        value: orderline => orderline.orderLineNumber,
      },
      {
        header: 'Model Name',
        value: orderline => orderline.modelNameCsv,
      },
      {
        header: 'GMAWB Handler',
        value: _ => selectedNewHandler?.displayName,
      },
      {
        header: 'Target Submission Date',
      },
      {
        header: 'Preferred Test Report',
      },
      {
        header: 'Certification Scheme',
        value: orderline => orderline.certificateScheme,
      },
      {
        header: 'Delivery Path',
        value: orderline => orderline.deliveryPath,
      },
      {
        header: 'Technology',
        value: orderline => orderline.technologyCsv,
      },
      {
        header: 'Special Instructions',
      },
      {
        header: 'GMAWB Link',
        value: orderline => {
          const encodedName = fixedEncodeURIComponent(orderline.gmaWBProjectName!)
          const url = new URL(
            `/project/${encodedName}`,
            process.env.REACT_APP_GMAWB_ALL_PROJECTS_URL
          )

          return `<a href="${url}">Click here</a>`
        },
        rawHtml: true,
      },
    ]

    const buildColumnHeaders = () => html`
      <tr>
        ${columns.map(column => html`<th style="${CELL_STYLE}">${column.header}</th>`).join('')}
      </tr>
    `

    const buildOrderLineRows = () =>
      orderlines
        .map(
          orderline => html`
            <tr>
              ${columns
                .map(column => {
                  const value = column.value ? column.value(orderline) : ''

                  return html`
                    <td style="${CELL_STYLE}">${column.rawHtml ? value : (value ?? '')}</td>
                  `
                })
                .join('')}
            </tr>
          `
        )
        .join('')

    const optionalFields = [
      'iPro Approver',
      'Output power information',
      'Link to Job Folder',
      'Link to application documents',
      'Notes',
    ]

    return html`<p><i>Hello ${selectedNewHandler?.displayName},</i></p>
      <div>
        <p><i>You have been assigned as the GMAWB Handler for the following order lines:</i></p>
        <table style="border-collapse: collapse;">
          <thead>
            ${buildColumnHeaders()}
          </thead>
          <tbody>
            ${buildOrderLineRows()}
          </tbody>
        </table>
      </div>

      <div>
        <ol style="font-size: 15px;">
          ${optionalFields.map(field => html`<li>${field} :</li>`).join('')}
        </ol>
      </div>

      <p>
        <i>
          Please review the details and initiate any necessary actions promptly. Click the link
          above to access the order line details directly.
        </i>
      </p>

      <div>
        <p>
          <i>
            Best Regards,<br />
            UL: GMA Workbench Team
          </i>
        </p>
      </div>`
  }

  const buildDefaultMail = ({
    selectedNewHandler,
    orderlines,
    loggedInUserEmail,
  }: BuildDefaultMailProps): MailModel => {
    return {
      to: [
        {
          email: selectedNewHandler?.normalizedEmail!,
        },
      ],
      cc:
        loggedInUserEmail &&
        loggedInUserEmail.toLowerCase() !== selectedNewHandler?.normalizedEmail.toLowerCase()
          ? [
              {
                email: loggedInUserEmail,
              },
            ]
          : [],
      subject: buildDefaultSubject({ orderlines }),
      body: buildDefaultBody({ selectedNewHandler, orderlines }),
      attachments: [],
    }
  }

  const openEmailEditor = async (
    orderlines: PmrE2EProjectDetailsOrderlineModel[],
    selectedNewHandler: E2EUserPaginatedModel,
    draft?: MailModel
  ) => {
    const currentHandlersEmails = orderlines
      .map(orderline => orderline.gmaWBHandlerEmail)
      .filter((email): email is string => !!email?.trim())
    const currentHandlersEmailSettings = await UserSettingsService.getByEmails(
      uniq(currentHandlersEmails)
    )

    const getUserSettings = (email: Nullable<string>) => {
      if (!email || !currentHandlersEmailSettings.data) {
        return undefined
      }

      return currentHandlersEmailSettings.data.find(user =>
        [user.emailAddress?.toLowerCase(), user.preferredEmailAddress?.toLowerCase()].includes(
          email?.toLowerCase()
        )
      )
    }

    const newEmailEditorOrderlines = orderlines.filter(orderline => {
      const currentHandlerSettings = getUserSettings(orderline.gmaWBHandlerEmail)
      return !currentHandlerSettings?.suppressGMAWBHandlerAssignmentEmail
    })

    setEmailEditorOrderLines(newEmailEditorOrderlines)

    if (newEmailEditorOrderlines.length === 0 && onSuppressed) {
      onSuppressed()
      return
    }

    setEmailEditorVisible(true)

    const initialValues =
      draft ??
      buildDefaultMail({
        loggedInUserEmail: loggedInUserEmail!,
        selectedNewHandler,
        orderlines: newEmailEditorOrderlines,
      })

    emailEditorRef?.current?.setInitialValues(initialValues)
  }

  const closeEmailEditor = () => {
    setEmailEditorVisible(false)
  }

  const showSaveAndCloseToastNotification = () => {
    showSuccess(
      'Email Draft Saved Successfully',
      'Your email draft has been successfully saved. Access the saved draft anytime from the GMAWB Handler column in Order Line Details.'
    )
  }

  const showCancelToastNotification = () => {
    showInfo('GMAWB Handler Update Cancelled', 'No changes were made to the GMAWB Handler.')
  }

  return {
    emailEditorVisible,
    emailEditorOrderlines,
    openEmailEditor,
    closeEmailEditor,
    showCancelToastNotification,
    setEmailEditorVisible,
    showSaveAndCloseToastNotification,
  }
}
